import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Paragraph, Text, H3 } from 'components/core/Typography';
import { Expand } from '@sparefoot/react-commons';
import { MarkdownWrapper } from 'components/core/MarkdownWrapper';
import { Button } from 'components/core/Button';
import { Link } from 'components/core/Link';
import { Chevron } from "components/core/Icons";
import FeaturesItem from 'components/facility/FacilityAmenitiesAccordion/FeaturesItem';
import AccessHours from 'components/facility/FacilityAmenitiesAccordion/AccessHours';

import './accordion.scss';

export function AccordionItem({
	question,
	answer,
	className,
	link,
	preserve,
	type,
	expanded: controlledExpanded,
	onToggle,
	toggleIndex,
}) {
	const [localExpanded, setLocalExpanded] = useState(false);
	const isControlled = controlledExpanded !== undefined;
	const expanded = isControlled ? controlledExpanded : localExpanded;

	const headingId = `accordion-heading-${question.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;
	const regionId = `accordion-region-${question.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;

	const handleToggle = () => {
		if (isControlled && onToggle) {
			onToggle(toggleIndex);
		} else {
			setLocalExpanded((prev) => !prev);
		}
	};

	return (
		<div
			className={classnames(className, 'accordion-item', {
				expanded,
			})}
		>
			<Button
				id={headingId}
				onClick={handleToggle}
				className="expand-button"
				aria-expanded={expanded}
				aria-controls={regionId}
			>
				<Text
					weight="bold"
					className="item-title"
				>
					<H3 size="medium">{question}</H3>
				</Text>
				<Chevron
					rotate={expanded ? 'up' : 'down'}
					fill='black'
					size='large'
					alt={expanded ? 'Collapse section' : 'Expand section'}
				/>
			</Button>
			<Expand
				expanded={expanded}
				preserve={preserve}
			>
				<div
					id={regionId}
					role="region"
					aria-labelledby={headingId}
				>
					<Paragraph
						weight="light"
						last
						className="item-answer"
					>
						{type === 'facility' && (
							<FeaturesItem featuresData={answer} />
						)}
						{type === 'accessHours' && (
							<AccessHours
								accessHours={answer.accessHours}
								officeHours={answer.officeHours}
							/>
						)}
						{type !== 'facility' && type !== 'accessHours' && (
							<MarkdownWrapper
								content={[
									{ type: 'faq_content', value: answer },
								]}
							/>
						)}
					</Paragraph>
					{link && (
						<H3
							weight="bold"
							last
							className="copy-link"
						>
							<Link href={link}>More info on {question}</Link>
						</H3>
					)}
				</div>
			</Expand>
		</div>
	);
}

AccordionItem.propTypes = {
	question: PropTypes.string.isRequired,
	answer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
	className: PropTypes.string,
	link: PropTypes.string,
	preserve: PropTypes.bool,
	type: PropTypes.string,
	expanded: PropTypes.bool,
	onToggle: PropTypes.func,
	toggleIndex: PropTypes.number,
};

export default AccordionItem;
